import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const [orderId, setOrderId] = useState(null);
  const [kind, setKind] = useState("order");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (attempts.current >= 8) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setOrderId(data.order_id);
        setKind(data.kind || "order");
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.status === "expired" || data.payment_status === "failed") { setState("error"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="App min-h-screen bg-sugar-bg"><Navbar />
      <div className="mx-auto max-w-md px-6 py-24 text-center">
        {state === "checking" && (<>
          <Loader2 size={48} className="mx-auto animate-spin text-sugar-pink" />
          <h1 data-testid="payment-checking" className="mt-6 font-display text-2xl font-bold text-sugar-ink">Confirming your payment…</h1>
          <p className="mt-2 text-sugar-ink2">Hang tight, this only takes a moment.</p>
        </>)}
        {state === "paid" && (<>
          <CheckCircle2 size={56} className="mx-auto text-status-delivered" />
          <h1 data-testid="payment-success" className="mt-6 font-display text-3xl font-extrabold text-sugar-ink">{kind === "balance" ? "Balance paid!" : "Order placed!"}</h1>
          <p className="mt-2 text-sugar-ink2">{kind === "balance" ? "Thank you! Your order is all paid up." : "Thank you! Sarah will mark your order as received once she's seen it. You can follow your cake's journey any time."}</p>
          <div className="mt-7 flex flex-col gap-3">
            <Button data-testid="success-track-btn" onClick={() => navigate(orderId ? `/track/${orderId}` : "/track")} className="rounded-full bg-sugar-pink py-6 font-bold text-white hover:bg-sugar-pinkhover">Track my order</Button>
            <Button variant="ghost" onClick={() => navigate("/")} className="rounded-full font-semibold text-sugar-ink2">Back home</Button>
          </div>
        </>)}
        {(state === "timeout" || state === "error") && (<>
          <XCircle size={48} className="mx-auto text-amber-500" />
          <h1 className="mt-6 font-display text-2xl font-bold text-sugar-ink">{state === "timeout" ? "Still processing" : "Something went wrong"}</h1>
          <p className="mt-2 text-sugar-ink2">Check your orders in a minute — if payment went through, it'll appear there.</p>
          <Button onClick={() => navigate("/track")} className="mt-6 rounded-full bg-sugar-pink px-8 py-6 font-bold text-white hover:bg-sugar-pinkhover">View my orders</Button>
        </>)}
      </div>
    </div>
  );
}
